import { createFileRoute, redirect } from '@tanstack/react-router';
import { MewsTeam, RouteType } from '@mews/enums';
import { CustomerPaymentsTab, PaymentsTabActiveSegment } from '@mews-commander/modules/src/modules/payment';
import { ArticlePath, createGuideLink, FeatureFlag, TextKey } from '@mews-commander/enums';
import { GuideLinkButton, PageHeaderActionsPortal, SpacedDash } from '@mews-commander/ui';
import { getFullName } from '@mews-commander/utils';

const segmentTitleTextKey = {
    [PaymentsTabActiveSegment.Overview]: TextKey.PaymentsTabSegmentTitleOverview,
    [PaymentsTabActiveSegment.Requests]: TextKey.PaymentRequestPlural,
    [PaymentsTabActiveSegment.Methods]: TextKey.PaymentMethodPlural,
    [PaymentsTabActiveSegment.Plans]: TextKey.PaymentPlanPlural,
};

export const Route = createFileRoute(
    '/_authenticated/$currentEnterpriseId/Customer/$id/_pageLayout/Payment/$activeSegment'
)({
    beforeLoad: ({ context: { featureFlagService }, params: { currentEnterpriseId, id, activeSegment } }) => {
        if (!featureFlagService.hasFlag(FeatureFlag.newCustomerPageRouteStructureEnabled)) {
            const legacyParams = { currentEnterpriseId, accountId: id };
            switch (activeSegment) {
                case PaymentsTabActiveSegment.Methods:
                    throw redirect({
                        to: `/$currentEnterpriseId/Payment/Methods/$accountId`,
                        params: legacyParams,
                    });
                case PaymentsTabActiveSegment.Requests:
                    throw redirect({
                        to: `/$currentEnterpriseId/Payment/Requests/$accountId`,
                        params: legacyParams,
                    });
                case PaymentsTabActiveSegment.Plans:
                    throw redirect({
                        to: `/$currentEnterpriseId/Payment/Plans/$accountId`,
                        params: legacyParams,
                    });
                case PaymentsTabActiveSegment.Overview:
                    throw redirect({
                        to: `/$currentEnterpriseId/Payment/Overview/$accountId`,
                        params: legacyParams,
                    });
                default:
                    throw redirect({
                        to: `/$currentEnterpriseId/Payment/Overview/$accountId`,
                        params: legacyParams,
                    });
            }
        }
    },
    loader: async ({ params: { id, activeSegment }, context: { ensureQueryData, localizeText } }) => {
        const { customers } = await ensureQueryData('accounts', 'getAll', {
            accountIds: [id],
        });
        const customer = customers?.[0];

        return {
            title: () =>
                `${getFullName(customer)}${SpacedDash}${localizeText({
                    textKey: segmentTitleTextKey[activeSegment as PaymentsTabActiveSegment],
                })}`.trim(),
        };
    },
    component: RouteComponent,
    staticData: {
        owner: MewsTeam.PaymentExperience,
        type: RouteType.Frontend,
    },
});

function RouteComponent() {
    const { id, activeSegment } = Route.useParams();

    return (
        <>
            <PageHeaderActionsPortal>
                <GuideLinkButton guideLink={createGuideLink(ArticlePath.TakePayment)} />
            </PageHeaderActionsPortal>
            <CustomerPaymentsTab accountId={id} activeSegment={activeSegment as PaymentsTabActiveSegment} contentOnly />
        </>
    );
}
